/** Relative urgency bucket for a task. Drives ordering and the due pill color. */
export type Due = 'overdue' | 'today' | 'soon' | 'later' | '';

export type TaskStatus = 'todo' | 'doing' | 'done' | 'stalled';

/** A unit of work within a client project (or loose, if projectId is empty). */
export interface Project {
  id: string;
  name: string;
}

/** An additional person associated with a client (beyond the primary contact). */
export interface ClientContact {
  id: string;
  name?: string;
  email?: string;
  role?: string;
}

/** An external reference (URL) attached to a task. */
export interface TaskLink {
  id: string;
  url: string;
  /** Optional display label; falls back to the hostname. */
  label?: string;
}

/** A file uploaded to a task (stored in Cloud Storage). */
export interface TaskAttachment {
  id: string;
  name: string;
  /** Cloud Storage download URL. */
  url: string;
  size?: number;
  contentType?: string;
}

export interface Task {
  id: string;
  clientId: string;
  title: string;
  /** Urgency bucket (derived from dueDate when present). */
  due: Due;
  /** Human label shown on the pill, e.g. "Today", "Fri", "2d late". */
  dueLabel: string;
  /** Concrete due date (YYYY-MM-DD), when set. Drives `due`/`dueLabel`. */
  dueDate?: string;
  /** Numeric mirror of `due` for cheap ordering in Firestore queries. */
  dueRank?: number;
  /** Owning project id, or '' for a loose task. */
  projectId: string;
  /** Free-form estimate, e.g. "1h", "45m". */
  est: string;
  status: TaskStatus;
  /** Rich description stored as Markdown. */
  notes: string;
  /** External links attached to the task. */
  linkItems?: TaskLink[];
  /** Files attached to the task. */
  attachments?: TaskAttachment[];
  /** @deprecated legacy free-form link summary; superseded by linkItems. */
  links?: string;
  /** Whether this task is a good candidate to hand to a coding agent. */
  ai?: boolean;
  /** In the transient "Lineup" — the working set of what to do now. */
  inLineup?: boolean;
  /** Sort position within the Lineup (append order). */
  lineupOrder?: number;
  done: boolean;
  createdAt?: number;

  /** Priority (Linear scale): 0=None, 1=Urgent, 2=High, 3=Medium, 4=Low. */
  priority?: number;
  /** Free-form labels carried over from the source system. */
  labels?: string[];
  /** Origin system for imported tasks, e.g. "linear". */
  source?: string;
  /** Original id in the source system, e.g. "HNRNP-3". */
  sourceId?: string;
  /** Deep link back to the source record. */
  sourceUrl?: string;
}

export interface Client {
  id: string;
  name: string;
  initials: string;
  /** CSS background (gradient) for the avatar tile (fallback when no logo). */
  avatarBg: string;
  /** Uploaded logo (Cloud Storage download URL); shown instead of initials. */
  logoUrl?: string;
  projects: Project[];
  /** Manual sort order in the grid. */
  order: number;

  /** Client CRM details. */
  website?: string;
  contactEmail?: string;
  phone?: string;
  notes?: string;

  /** Extra domains (beyond `website`) used to route inbound email to this client. */
  additionalDomains?: string[];
  /** Extra people associated with this client. */
  additionalContacts?: ClientContact[];
}

/** A client with its tasks resolved — the shape the board renders. */
export interface ClientWithTasks extends Client {
  tasks: Task[];
}

/** A connected Slack workspace (a doc under `slackConnections/{uid}/workspaces`). */
export interface SlackWorkspaceConnection {
  teamId: string;
  teamName: string;
  /** CRM client this workspace is tagged to, for deterministic triage
   *  pre-assignment. `null` for general/internal workspaces with no 1:1 client. */
  clientId: string | null;
  /** True if the stored refresh token failed and the workspace needs reconnecting. */
  needsReauth?: boolean;
}

export interface TriageItem {
  id: string;
  title: string;
  source: string;
  /** Swatch color for the source dot. */
  sourceColor: string;
  /** Human "age" label, e.g. "12m ago". */
  age: string;
  resolved: boolean;
  createdAt?: number;
  /** Client this item was pre-assigned to (sender-domain match), if any. */
  suggestedClientId?: string;
  suggestedClientName?: string;
  /** Fuller context (e.g. email body) carried over into the task's notes on assignment. */
  notes?: string;
  /** Gmail message id, or a Slack message permalink, when this item came
   *  from email/Slack (for de-duplication). */
  sourceRef?: string;
  /** Slack workspace (team) id, when this item came from Slack. */
  sourceTeamId?: string;
  /** Files fetched at scan time (e.g. "$CRM" email attachments), carried
   *  over onto the task once this item is assigned. */
  attachments?: TaskAttachment[];
}

export const DUE_RANK: Record<Due, number> = {
  overdue: 0,
  today: 1,
  soon: 2,
  later: 3,
  '': 4,
};
